import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import {
  ChevronLeft, ChevronRight, Lock, Trash2, Leaf,
  TrendingUp, User, Settings, Bell, ArrowLeft, Moon, LogOut,
  ShieldCheck, ShieldOff, Camera, X, Sparkles
} from 'lucide-react';
import { AiGate } from './AiGate';
import { ProfileAvatar } from './ProfileAvatar';
import { AI_FEATURES } from '../utils/aiFeatures';
import { SUPPORTED_LANGUAGES, normalizeLanguageCode } from '../utils/languages';
import { GrowthTimeline } from './GrowthTimeline';
import api from '../utils/api';
import { setAiConsent } from '../utils/aiConsent';
import { subscribeToPush, getCurrentPushSubscription } from '../utils/pushManager';

// ─── Shared card shell ───────────────────────────────────────────────────────
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-[2rem] shadow-sm border border-stone-100 p-6 transition-all hover:shadow-md ${className}`}>
    {children}
  </div>
);

const SectionHeading = ({ children }) => (
  <h3 className="text-[11px] font-bold text-stone-400 uppercase tracking-[0.18em] border-b border-stone-100 pb-2 mb-4">
    {children}
  </h3>
);

const FieldLabel = ({ children, required }) => (
  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">
    {children}{required && <span className="text-red-400 ml-0.5">*</span>}
  </label>
);

const TextInput = ({ type = 'text', value, onChange, placeholder, invalid }) => (
  <input
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    className={`w-full px-3 py-2.5 rounded-xl outline-none text-sm font-semibold text-stone-800 focus:ring-2 transition
      ${invalid ? 'bg-red-50 ring-red-300 focus:ring-red-300' : 'bg-stone-50 focus:ring-stone-200'}`}
  />
);

const SelectInput = ({ value, onChange, children }) => (
  <select
    value={value}
    onChange={onChange}
    className="w-full px-3 py-2.5 bg-stone-50 rounded-xl outline-none text-sm font-semibold text-stone-800 focus:ring-2 focus:ring-stone-200 transition"
  >
    {children}
  </select>
);

// ─── Month names ─────────────────────────────────────────────────────────────
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

// ─── TAB 1: Journal Stats ─────────────────────────────────────────────────────
function JournalStatsTab({ userId, onNavigateToJournal }) {
  const now = new Date();
  const currentYear  = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-based
  const [year,  setYear]  = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [data,  setData]  = useState(null);   // null = loading
  const [fetchError, setFetchError] = useState('');
  const [cache, setCache] = useState({});

  const fetchStats = useCallback(async (y, m) => {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    if (cache[key]) { setData(cache[key]); setFetchError(''); return; }
    setData(null);
    setFetchError('');
    try {
      const res = await api.get(`/user/${userId}/journal-stats?year=${y}&month=${m}`);
      setCache(prev => ({ ...prev, [key]: res.data }));
      setData(res.data);
    } catch (err) {
      setFetchError(err?.response?.data?.error || 'Could not load stats. Please try again.');
      setData({ stats: [], totals: { journals: 0, guided: 0 } });
    }
  }, [userId, cache]);

  useEffect(() => { fetchStats(year, month); }, [year, month]); // eslint-disable-line

  const isAtCurrentMonth = year === currentYear && month === currentMonth;

  const navigateMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1)  { m = 12; y--; }
    if (m > 12) { m = 1;  y++; }
    if (y > currentYear || (y === currentYear && m > currentMonth)) return;
    setMonth(m); setYear(y);
  };

  // Year options: from 2023 up to current year
  const yearOptions = [];
  for (let y = 2023; y <= currentYear; y++) yearOptions.push(y);

  // Month options: if selected year is current year, only up to current month
  const maxMonth = year === currentYear ? currentMonth : 12;
  const monthOptions = MONTH_NAMES.slice(0, maxMonth);

  // Build a Set of { date -> type } from stats
  const dayMap = {};
  if (data) {
    for (const row of data.stats) {
      const existing = dayMap[row.date];
      // guided takes visual priority; if already guided keep it
      if (!existing || existing === 'journal') {
        dayMap[row.date] = row.type;
      }
    }
  }

  // Build calendar grid cells
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDow    = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const cells = [];
  // Leading empty cells
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const dayClass = (type) => {
    if (type === 'guided') {
      return 'bg-gradient-to-br from-amber-100 via-yellow-200 to-amber-100 text-amber-800 font-semibold shadow-[0_0_10px_rgba(251,191,36,0.4)] border border-amber-200';
    }
    if (type === 'journal') {
      return 'bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold';
    }
    return 'bg-stone-50/60 text-stone-400';
  };

  return (
    <div className="space-y-2 animate-in fade-in duration-300">
      {/* Month/year navigator */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigateMonth(-1)}
          aria-label="Previous month"
          className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-500 transition flex-shrink-0"
        >
          <ChevronLeft size={16} />
        </button>

        {/* Month dropdown */}
        <select
          value={month}
          onChange={e => setMonth(Number(e.target.value))}
          aria-label="Select month"
          className="flex-1 min-w-0 px-2 py-1.5 bg-white border border-stone-200 rounded-lg text-xs font-semibold text-stone-700 outline-none focus:ring-2 focus:ring-stone-200 appearance-none cursor-pointer"
        >
          {monthOptions.map((name, i) => (
            <option key={i + 1} value={i + 1}>{name}</option>
          ))}
        </select>

        {/* Year dropdown */}
        <select
          value={year}
          onChange={e => {
            const y = Number(e.target.value);
            setYear(y);
            if (y === currentYear && month > currentMonth) setMonth(currentMonth);
          }}
          aria-label="Select year"
          className="w-20 px-2 py-1.5 bg-white border border-stone-200 rounded-lg text-xs font-semibold text-stone-700 outline-none focus:ring-2 focus:ring-stone-200 appearance-none cursor-pointer"
        >
          {yearOptions.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <button
          onClick={() => navigateMonth(1)}
          aria-label="Next month"
          disabled={isAtCurrentMonth}
          className={`p-1.5 rounded-lg transition flex-shrink-0 ${isAtCurrentMonth ? 'text-stone-200 cursor-not-allowed' : 'hover:bg-stone-100 text-stone-500'}`}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Error state */}
      {fetchError && (
        <p className="text-xs text-red-500 text-center px-2">{fetchError}</p>
      )}

      {/* Totals summary */}
      <div className="flex justify-center gap-4 text-[11px] font-bold tracking-widest uppercase text-stone-500">
        <span>
          <span className="inline-block w-2 h-2 rounded-sm bg-emerald-300 mr-1 align-middle" />
          Journals: {data ? data.totals.journals : '—'}
        </span>
        <span className="text-stone-300">|</span>
        <span>
          <span className="inline-block w-2 h-2 rounded-sm bg-amber-300 mr-1 align-middle" />
          Guided Reflections: {data ? data.totals.guided : '—'}
        </span>
      </div>

      {/* Calendar grid */}
      <Card className="p-3">
        {/* Day-of-week headers */}
        <div className="grid grid-cols-7 mb-1">
          {['S','M','T','W','T','F','S'].map((d, i) => (
            <div key={i} className="text-center text-[10px] font-bold text-stone-400 py-0.5">{d}</div>
          ))}
        </div>

        {data === null ? (
          /* Skeleton */
          <div className="grid grid-cols-7 gap-0.5">
            {Array.from({ length: 35 }).map((_, i) => (
              <div key={i} className="h-8 rounded-lg bg-stone-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((day, i) => {
              if (!day) return <div key={`e-${i}`} />;
              const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const type = dayMap[dateStr];
              const isToday = dateStr === new Date().toLocaleDateString('en-CA');
              const isClickable = !!type && !!onNavigateToJournal;
              return (
                <div
                  key={day}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  aria-label={isClickable ? `Open ${type === 'guided' ? 'guided reflection' : 'journal'} for ${dateStr}` : undefined}
                  title={type ? (type === 'guided' ? 'Guided Reflection' : 'Journal') : undefined}
                  onClick={isClickable ? () => onNavigateToJournal(dateStr) : undefined}
                  onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onNavigateToJournal(dateStr); } : undefined}
                  className={`h-8 rounded-lg flex items-center justify-center text-xs transition-all
                    ${dayClass(type)}
                    ${isToday && !type ? 'ring-2 ring-stone-300' : ''}
                    ${isClickable ? 'cursor-pointer hover:scale-110 hover:shadow-md active:scale-95' : ''}
                  `}
                >
                  {day}
                </div>
              );
            })}
          </div>
        )}

        {/* Legend */}
        <div className="flex gap-4 mt-2 pt-2 border-t border-stone-100 justify-center">
          <span className="flex items-center gap-1 text-[10px] text-stone-500">
            <span className="w-2.5 h-2.5 rounded-sm bg-emerald-100 border border-emerald-300 inline-block" />
            Journal
          </span>
          <span className="flex items-center gap-1 text-[10px] text-stone-500">
            <span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-amber-100 to-yellow-200 border border-amber-200 inline-block" />
            Guided Reflection
          </span>
        </div>
      </Card>
    </div>
  );
}

// ─── TAB 2: Identity ─────────────────────────────────────────────────────────
function IdentityTab({ profile, isProfileDirty, isNameValid, updateProfileField, persistProfile, onProfileReset }) {
  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      {/* Unsaved changes bar */}
      {isProfileDirty && (
        <div className="sticky top-16 z-20 bg-white/80 backdrop-blur border border-stone-200 rounded-2xl px-4 py-3 flex items-center justify-between shadow-sm">
          <span className="text-sm font-medium text-stone-600">You have unsaved changes</span>
          <div className="flex gap-3">
            <button
              onClick={onProfileReset}
              className="px-4 py-2 rounded-xl bg-stone-200 text-stone-700 font-semibold hover:bg-stone-300 transition"
            >
              Cancel
            </button>
            <button
              onClick={() => persistProfile(profile)}
              disabled={!isNameValid}
              className={`px-4 py-2 rounded-xl font-semibold transition shadow
                ${isNameValid ? 'bg-stone-800 text-white hover:bg-black' : 'bg-stone-300 text-stone-500 cursor-not-allowed'}`}
            >
              Save
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Personal */}
        <Card className="space-y-4">
          <SectionHeading>Personal Details</SectionHeading>
          <div className="space-y-4">
            <div>
              <FieldLabel required>Full Name</FieldLabel>
              <TextInput
                value={profile.name}
                onChange={e => updateProfileField('name', e.target.value)}
                invalid={!isNameValid && isProfileDirty}
              />
            </div>
            <div>
              <FieldLabel>Date of Birth</FieldLabel>
              <TextInput type="date" value={profile.dob} onChange={e => updateProfileField('dob', e.target.value)} />
            </div>
            <div>
              <FieldLabel>Gender</FieldLabel>
              <SelectInput value={profile.gender} onChange={e => updateProfileField('gender', e.target.value)}>
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </SelectInput>
            </div>
            <div>
              <FieldLabel>Preferred Language</FieldLabel>
              {/* Options come straight off the shared registry, so a new
                  language ships without touching this file. Labels are in the
                  language itself — a Malayalam speaker shouldn't have to read
                  English to find Malayalam. Always has a value (English), so
                  there is no empty "Select" state. */}
              <SelectInput
                value={normalizeLanguageCode(profile.preferred_language)}
                onChange={e => updateProfileField('preferred_language', e.target.value)}
              >
                {SUPPORTED_LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code} lang={lang.code}>
                    {lang.nativeLabel}
                  </option>
                ))}
              </SelectInput>
              <p className="mt-1 text-[11px] text-stone-400 leading-relaxed">
                Used across the app, and for replies when you write with AI.
              </p>
            </div>
            <div>
              <FieldLabel>Occupation</FieldLabel>
              <TextInput value={profile.occupation} onChange={e => updateProfileField('occupation', e.target.value)} />
            </div>
          </div>
        </Card>

        {/* Contact */}
        <Card className="space-y-4">
          <SectionHeading>Contact & Location</SectionHeading>
          <div className="space-y-4">
            <div>
              <FieldLabel>City</FieldLabel>
              <TextInput value={profile.city} onChange={e => updateProfileField('city', e.target.value)} />
            </div>
            <div>
              <FieldLabel>Cellphone</FieldLabel>
              <TextInput type="tel" value={profile.cellphone} onChange={e => updateProfileField('cellphone', e.target.value)} />
            </div>
          </div>
        </Card>

        {/* Goals */}
        <Card className="md:col-span-2 space-y-4">
          <SectionHeading>Goals & Interests</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FieldLabel>Main Goal</FieldLabel>
              <TextInput value={profile.goal} onChange={e => updateProfileField('goal', e.target.value)} />
            </div>
            <div>
              <FieldLabel>Key Activities</FieldLabel>
              <TextInput value={profile.activities} onChange={e => updateProfileField('activities', e.target.value)} />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

// One choice in the pause group. Selection is carried by border, tint *and* a
// radio mark — never colour alone, so it reads the same to a colour-blind user.
function PauseOption({ selected, disabled, onSelect, title, body, destructive = false }) {
  const accent = destructive
    ? 'border-amber-400 bg-amber-50'
    : 'border-emerald-400 bg-emerald-50';
  const dot = destructive ? 'border-amber-600 bg-amber-600' : 'border-emerald-600 bg-emerald-600';
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={`w-full text-left p-4 rounded-2xl border transition-all duration-300 ease-out disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2 ${
        selected ? accent : 'border-stone-200 bg-white hover:bg-stone-50'
      }`}
    >
      <span className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className={`shrink-0 mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
            selected ? dot : 'border-stone-300 bg-white'
          }`}
        >
          {selected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
        </span>
        <span>
          <span className="block text-sm font-semibold text-stone-800">{title}</span>
          <span className="block text-xs text-stone-500 leading-relaxed mt-1">{body}</span>
        </span>
      </span>
    </button>
  );
}

// ─── TAB 3: Safety ───────────────────────────────────────────────────────────
function SafetyTab({ userId, onLogout, aiDataConsent, onConsentChange }) {
  // Password change
  const [showPwPanel, setShowPwPanel] = useState(false);
  const [oldPass, setOldPass]         = useState('');
  const [newPass, setNewPass]         = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [pwError, setPwError]         = useState('');

  // Delete account
  const [showDeletePanel, setShowDeletePanel] = useState(false);
  const [deleteText, setDeleteText]           = useState('');
  const [isDeleting, setIsDeleting]           = useState(false);

  // Prune modal
  const [showPruneModal, setShowPruneModal] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [isPruning, setIsPruning]           = useState(false);
  const [pruneMsg, setPruneMsg]             = useState('');

  // AI consent toggle
  const [isTogglingConsent, setIsTogglingConsent] = useState(false);
  const [showPauseConfirm, setShowPauseConfirm] = useState(false);
  // Pausing keeps AI memory by default — erasure is not undoable, so it has to
  // be chosen deliberately rather than fallen into.
  const [clearMemory, setClearMemory] = useState(false);

  const openPauseConfirm = () => {
    setClearMemory(false);
    setShowPauseConfirm(true);
  };

  const handleDisableAi = async () => {
    setIsTogglingConsent(true);
    try {
      await setAiConsent(userId, { accepted: false, clearMemory });
      onConsentChange?.(false);
      setShowPauseConfirm(false);
    } catch {
      // silent — user can retry
    } finally {
      setIsTogglingConsent(false);
    }
  };

  const handlePasswordChange = async () => {
    setPwError('');
    if (!oldPass || !newPass || !confirmPass) { setPwError('All fields are required.'); return; }
    if (newPass !== confirmPass) { setPwError('New passwords do not match.'); return; }
    try {
      const res = await api.post('/auth/change-password', {
        user_id: userId,
        oldPassword: oldPass,
        newPassword: newPass,
      });
      if (res.data.success) {
        // Other devices are now signed out; this one keeps going on the
        // token the change handed back.
        if (res.data.token) localStorage.setItem('token', res.data.token);
        setShowPwPanel(false);
        setOldPass(''); setNewPass(''); setConfirmPass('');
      } else {
        setPwError(res.data.error || 'Failed to update password.');
      }
    } catch {
      setPwError('Something went wrong. Please try again.');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteText !== 'DELETE') return;
    setIsDeleting(true);
    try {
      await api.delete(`/user/${userId}`, { data: { confirmText: 'DELETE' } });
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      onLogout();
    } catch {
      setIsDeleting(false);
      alert('Could not delete account. Please try again.');
    }
  };

  // Build list of past year-month pairs (from 2023-01 up to last month)
  const pastMonths = (() => {
    const list = [];
    const start = new Date(2023, 0, 1);
    const limit = new Date();
    limit.setDate(1);
    limit.setMonth(limit.getMonth() - 1); // last month
    let cur = new Date(start);
    while (cur <= limit) {
      list.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`);
      cur.setMonth(cur.getMonth() + 1);
    }
    return list.reverse(); // most recent first
  })();

  const toggleMonth = (m) =>
    setSelectedMonths(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);

  const handlePrune = async () => {
    if (selectedMonths.length === 0) return;
    setIsPruning(true);
    setPruneMsg('');
    try {
      const res = await api.delete(`/user/${userId}/journals`, { data: { months: selectedMonths } });
      setPruneMsg(`${res.data.deleted} entries removed.`);
      setSelectedMonths([]);
    } catch {
      setPruneMsg('Something went wrong. Please try again.');
    } finally {
      setIsPruning(false);
    }
  };

  const formatMonthLabel = (ym) => {
    const [y, m] = ym.split('-');
    return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      {/* Change Password */}
      <Card className="space-y-4">
        <SectionHeading>Security</SectionHeading>
        {!showPwPanel ? (
          <button
            onClick={() => setShowPwPanel(true)}
            className="w-full flex items-center gap-3 px-4 py-3 bg-stone-800 text-white rounded-xl font-semibold shadow-sm hover:bg-black transition-all"
          >
            <Lock size={16} /> Change Password
          </button>
        ) : (
          <div className="space-y-3 p-4 bg-stone-50 rounded-2xl border border-stone-200 animate-in fade-in slide-in-from-top-2">
            {pwError && <p className="text-sm text-red-500 font-medium">{pwError}</p>}
            <TextInput type="password" value={oldPass} onChange={e => setOldPass(e.target.value)} placeholder="Current password" />
            <TextInput type="password" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="New password" />
            <TextInput type="password" value={confirmPass} onChange={e => setConfirmPass(e.target.value)} placeholder="Confirm new password" />
            <div className="flex gap-3">
              <button onClick={handlePasswordChange} className="flex-1 py-3 bg-stone-800 text-white rounded-xl font-semibold hover:bg-black transition-all">
                Update
              </button>
              <button
                onClick={() => { setShowPwPanel(false); setOldPass(''); setNewPass(''); setConfirmPass(''); setPwError(''); }}
                className="flex-1 py-3 bg-stone-200 text-stone-700 rounded-xl font-semibold hover:bg-stone-300 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Prune Journal Data */}
      <Card className="space-y-4">
        <SectionHeading>Data Management</SectionHeading>
        <button
          onClick={() => { setShowPruneModal(true); setPruneMsg(''); setSelectedMonths([]); }}
          className="w-full flex items-center gap-3 px-4 py-3 bg-stone-100 text-stone-700 border border-stone-200 rounded-xl font-semibold hover:bg-stone-200 transition-all"
        >
          <Leaf size={16} className="text-emerald-600" /> Prune Your Journey
        </button>
        <p className="text-xs text-stone-400 leading-relaxed">
          Select months of past journals to permanently remove. Your reflections are yours — clear what no longer serves you.
        </p>
      </Card>

      {/* AI Features */}
      <Card className="space-y-4">
        <SectionHeading>AI Features</SectionHeading>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`shrink-0 p-2 rounded-xl ${aiDataConsent === true ? 'bg-emerald-50' : 'bg-stone-100'}`}>
              {aiDataConsent === true
                ? <ShieldCheck size={16} className="text-emerald-600" />
                : <ShieldOff size={16} className="text-stone-400" />}
            </div>
            <div>
              <p className="text-sm font-semibold text-stone-700">
                {aiDataConsent === true ? 'Active' : 'Paused'}
              </p>
              <p className="text-xs text-stone-400 leading-relaxed mt-0.5">
                {aiDataConsent === true
                  ? 'Your journal content is shared with an AI model for chat and tagging.'
                  : 'AI chat and auto-tagging are paused. Your data stays local.'}
              </p>
            </div>
          </div>
        </div>
        {aiDataConsent === true ? (
          showPauseConfirm ? (
            <div className="space-y-4 p-4 bg-stone-50 rounded-2xl border border-stone-200 animate-in fade-in slide-in-from-top-2">
              <p className="text-xs text-stone-500 leading-relaxed">
                Your journals stay safe and unchanged either way. Choose what happens to what the AI has already learned.
              </p>
              <div className="space-y-3" role="radiogroup" aria-label="What happens to AI memory">
                <PauseOption
                  selected={!clearMemory}
                  disabled={isTogglingConsent}
                  onSelect={() => setClearMemory(false)}
                  title="Pause only"
                  body="Stop using new entries for AI. What it already learned stays saved, so turning it back on picks up where you left off."
                />
                <PauseOption
                  selected={clearMemory}
                  disabled={isTogglingConsent}
                  onSelect={() => setClearMemory(true)}
                  destructive
                  title="Pause and clear AI memory"
                  body="Stop using new entries and delete everything the AI has learned so far. This cannot be undone."
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleDisableAi}
                  disabled={isTogglingConsent}
                  className={`flex-1 py-3 text-white rounded-xl font-semibold transition-all duration-300 ease-out disabled:opacity-50 ${
                    clearMemory ? 'bg-amber-600 hover:bg-amber-700' : 'bg-stone-800 hover:bg-black'
                  }`}
                >
                  {isTogglingConsent
                    ? (clearMemory ? 'Clearing…' : 'Pausing…')
                    : (clearMemory ? 'Pause and clear' : 'Pause')}
                </button>
                <button
                  onClick={() => setShowPauseConfirm(false)}
                  disabled={isTogglingConsent}
                  className="flex-1 py-3 bg-white text-stone-700 border border-stone-200 rounded-xl font-semibold hover:bg-stone-50 transition-all duration-300 ease-out"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={openPauseConfirm}
              disabled={isTogglingConsent}
              className="w-full flex items-center gap-3 px-4 py-3 bg-stone-100 text-stone-600 border border-stone-200 rounded-xl font-semibold hover:bg-stone-200 transition-all disabled:opacity-50"
            >
              <ShieldOff size={15} /> Pause AI features
            </button>
          )
        ) : (
          <button
            onClick={() => onConsentChange?.(true)}
            disabled={isTogglingConsent}
            className="w-full flex items-center gap-3 px-4 py-3 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl font-semibold hover:bg-emerald-100 transition-all"
          >
            <ShieldCheck size={15} /> Enable AI features
          </button>
        )}
        <p className="text-xs text-stone-400 leading-relaxed">
          Data is encrypted at rest and sent only for processing. You can change this at any time.
        </p>
      </Card>

      {/* Delete Profile */}
      <Card className="space-y-4">
        <SectionHeading>Danger Zone</SectionHeading>
        {!showDeletePanel ? (
          <button
            onClick={() => setShowDeletePanel(true)}
            className="w-full flex items-center gap-3 px-4 py-3 bg-red-50 text-red-600 border border-red-200 rounded-xl font-semibold hover:bg-red-100 transition-all"
          >
            <Trash2 size={16} /> Delete Profile
          </button>
        ) : (
          <div className="space-y-3 p-4 bg-red-50 rounded-2xl border border-red-100 animate-in fade-in slide-in-from-top-2">
            <p className="text-sm text-red-700 leading-relaxed font-medium">
              This permanently removes your journals, reflections, and all memories. This cannot be undone.
            </p>
            <p className="text-xs text-red-500">Type <strong>DELETE</strong> to confirm</p>
            <input
              type="text"
              value={deleteText}
              onChange={e => setDeleteText(e.target.value)}
              placeholder="DELETE"
              className="w-full p-3 rounded-xl bg-white border border-red-200 outline-none font-mono text-red-700 focus:ring-2 focus:ring-red-300"
            />
            <div className="flex gap-3">
              <button
                onClick={handleDeleteAccount}
                disabled={deleteText !== 'DELETE' || isDeleting}
                className={`flex-1 py-3 rounded-xl font-semibold transition-all
                  ${deleteText === 'DELETE' && !isDeleting
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-stone-200 text-stone-400 cursor-not-allowed'}`}
              >
                {isDeleting ? 'Deleting…' : 'Confirm Delete'}
              </button>
              <button
                onClick={() => { setShowDeletePanel(false); setDeleteText(''); }}
                className="flex-1 py-3 bg-stone-200 text-stone-700 rounded-xl font-semibold hover:bg-stone-300 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Prune Modal */}
      {showPruneModal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Prune Your Journey"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm"
            onClick={() => !isPruning && setShowPruneModal(false)}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-[2rem] shadow-2xl w-full max-w-sm p-6 space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-emerald-50 rounded-xl"><Leaf size={18} className="text-emerald-600" /></div>
              <div>
                <h2 className="font-bold text-stone-800 text-lg leading-tight">Prune Your Journey</h2>
                <p className="text-xs text-stone-500 mt-1.5 leading-relaxed">
                  Select months of past journals to clear from your narrative. This action is permanent.
                </p>
              </div>
            </div>

            {pruneMsg && <p className="text-sm font-medium text-emerald-700 bg-emerald-50 rounded-xl p-3">{pruneMsg}</p>}

            {pastMonths.length === 0 ? (
              <p className="text-sm text-stone-400 text-center py-4">No past months available to prune.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
                {pastMonths.map(m => (
                  <label key={m} className="flex items-center gap-2 cursor-pointer p-2 rounded-xl hover:bg-stone-50 transition">
                    <input
                      type="checkbox"
                      checked={selectedMonths.includes(m)}
                      onChange={() => toggleMonth(m)}
                      className="accent-stone-700 w-4 h-4 rounded"
                    />
                    <span className="text-sm font-medium text-stone-700">{formatMonthLabel(m)}</span>
                  </label>
                ))}
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <button
                onClick={() => !isPruning && setShowPruneModal(false)}
                className="flex-1 py-3 bg-stone-100 text-stone-700 rounded-xl font-semibold hover:bg-stone-200 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handlePrune}
                disabled={selectedMonths.length === 0 || isPruning}
                className={`flex-1 py-3 rounded-xl font-semibold transition-all
                  ${selectedMonths.length > 0 && !isPruning
                    ? 'bg-stone-800 text-white hover:bg-black'
                    : 'bg-stone-200 text-stone-400 cursor-not-allowed'}`}
              >
                {isPruning ? 'Pruning…' : `Confirm (${selectedMonths.length})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TAB 4: Deep Insights ────────────────────────────────────────────────────

const EMOTIONS = [
  { key: 'calm',        label: 'Calm / peace',         color: '#8FBC9F' },
  { key: 'curiosity',   label: 'Curiosity / hope',     color: '#7BAEC8' },
  { key: 'joy',         label: 'Joy / gratitude',      color: '#F0CC7A' },
  { key: 'frustration', label: 'Frustration / stress', color: '#F2A97A' },
  { key: 'sadness',     label: 'Sadness / longing',    color: '#E8A0A0' },
];

// SVG chart layout
const W = 700, H = 240;
const PAD = { top: 24, right: 16, bottom: 36, left: 8 };
const CHART_W = W - PAD.left - PAD.right;
const CHART_H = H - PAD.top - PAD.bottom;

function xScale(i, n) {
  return PAD.left + (n > 1 ? (i / (n - 1)) : 0.5) * CHART_W;
}

function smoothAreaPath(topPts, bottomPts) {
  if (topPts.length === 0) return '';
  let d = `M ${topPts[0][0]},${topPts[0][1]}`;
  for (let i = 1; i < topPts.length; i++) {
    const cpx = (topPts[i - 1][0] + topPts[i][0]) / 2;
    d += ` C ${cpx},${topPts[i - 1][1]} ${cpx},${topPts[i][1]} ${topPts[i][0]},${topPts[i][1]}`;
  }
  const rev = [...bottomPts].reverse();
  d += ` L ${rev[0][0]},${rev[0][1]}`;
  for (let i = 1; i < rev.length; i++) {
    const cpx = (rev[i - 1][0] + rev[i][0]) / 2;
    d += ` C ${cpx},${rev[i - 1][1]} ${cpx},${rev[i][1]} ${rev[i][0]},${rev[i][1]}`;
  }
  return d + ' Z';
}

function formatWeekLabel(isoDate) {
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function DeepInsightsTab({ userId, aiDataConsent, onEnableAI }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [hoveredWeek, setHoveredWeek] = useState(null);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get(`/user/${userId}/insights/emotional-landscape`)
      .then(res => { setData(res.data.weeks); setFetchError(''); })
      .catch(() => setFetchError('Could not load your insights right now.'))
      .finally(() => setLoading(false));
  }, [userId]);

  // ── Derived values ─────────────────────────────────────────────────────────
  const totals = data
    ? EMOTIONS.reduce((acc, em) => {
        acc[em.key] = data.reduce((s, w) => s + (Number(w[em.key]) || 0), 0);
        return acc;
      }, {})
    : null;

  const maxTotal = data
    ? Math.max(...data.map(w => EMOTIONS.reduce((s, e) => s + (Number(w[e.key]) || 0), 0)), 1)
    : 1;

  const allZero = data ? Object.values(totals).every(v => v === 0) : false;

  const dominant = totals
    ? EMOTIONS.reduce((best, em) => (totals[em.key] > (totals[best.key] || 0) ? em : best), EMOTIONS[0])
    : null;

  const dominantWeeksCount = data && dominant
    ? data.filter(w => Number(w[dominant.key]) > 0).length
    : 0;

  const rangeCount = totals ? Object.values(totals).filter(v => v > 0).length : 0;

  // Find highest frustration week index (0-based)
  const peakFrustrationIdx = data
    ? data.reduce((best, w, i) =>
        Number(w.frustration) > Number(data[best]?.frustration ?? 0) ? i : best, 0)
    : null;

  const hasFrustrationShift =
    data && peakFrustrationIdx !== null &&
    Number(data[peakFrustrationIdx]?.frustration) > 0 &&
    peakFrustrationIdx < data.length - 1;

  // Find highest sadness week index (0-based)
  const peakSadnessIdx = data
    ? data.reduce((best, w, i) =>
        Number(w.sadness) > Number(data[best]?.sadness ?? 0) ? i : best, 0)
    : null;

  const hasSadnessShift =
    data && peakSadnessIdx !== null &&
    Number(data[peakSadnessIdx]?.sadness) > 0 &&
    peakSadnessIdx < data.length - 1;

  console.log("peakFrustrationIdx ", peakFrustrationIdx);
  console.log("hasFrustrationShift ", hasFrustrationShift);
  console.log("peakSadnessIdx ", peakSadnessIdx);
  console.log("hasSadnessShift ", hasSadnessShift);

  // ── Chart geometry ──────────────────────────────────────────────────────────
  const n = data?.length || 7;

  function buildPaths() {
    if (!data || allZero) return [];
    return EMOTIONS.map((em, ei) => {
      const topPts = data.map((w, wi) => {
        const cumTop = EMOTIONS.slice(0, ei + 1).reduce((s, e) => s + (Number(w[e.key]) || 0), 0);
        return [xScale(wi, n), PAD.top + CHART_H - (cumTop / maxTotal) * CHART_H];
      });
      const bottomPts = data.map((w, wi) => {
        const cumBottom = EMOTIONS.slice(0, ei).reduce((s, e) => s + (Number(w[e.key]) || 0), 0);
        return [xScale(wi, n), PAD.top + CHART_H - (cumBottom / maxTotal) * CHART_H];
      });
      return { em, path: smoothAreaPath(topPts, bottomPts) };
    });
  }

  const paths = buildPaths();

  function handleMouseMove(e) {
    if (!data) return;
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) / rect.width) * W;
    let closest = 0;
    let minDist = Infinity;
    data.forEach((_, i) => {
      const dist = Math.abs(mouseX - xScale(i, n));
      if (dist < minDist) { minDist = dist; closest = i; }
    });
    setHoveredWeek(closest);
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
    <AiGate feature={AI_FEATURES.deepInsights} consent={aiDataConsent} variant="full" onEnable={onEnableAI}>
    <div className="space-y-4 animate-in fade-in duration-300">
      <Card className="space-y-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <h3 className="text-base font-bold text-stone-800 leading-tight">Your emotional landscape</h3>
            <p className="text-[11px] text-stone-400 mt-0.5">Based on what you've shared over the past 7 weeks</p>
          </div>
          {data && !allZero && data[0] && data[data.length - 1] && (
            <span className="text-[11px] text-stone-400 font-medium shrink-0">
              {formatWeekLabel(data[0].week_start)} – {formatWeekLabel(data[data.length - 1].week_start)}
            </span>
          )}
        </div>

        {/* Legend */}
        {!loading && !allZero && (
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {EMOTIONS.map(em => (
              <span key={em.key} className="flex items-center gap-1.5 text-[11px] text-stone-600">
                <span className="w-2.5 h-2.5 rounded-sm inline-block flex-shrink-0" style={{ backgroundColor: em.color }} />
                {em.label}
              </span>
            ))}
          </div>
        )}

        {/* Chart area */}
        {loading && (
          <div className="space-y-2">
            <div className="h-[240px] bg-stone-50 rounded-2xl animate-pulse" />
            <div className="flex gap-2">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="flex-1 h-3 bg-stone-100 rounded animate-pulse" />
              ))}
            </div>
          </div>
        )}

        {!loading && fetchError && (
          <p className="text-sm text-stone-400 text-center py-8">{fetchError}</p>
        )}

        {!loading && !fetchError && allZero && (
          <div className="py-10 text-center space-y-2">
            <TrendingUp size={28} className="text-stone-200 mx-auto" />
            <p className="text-sm font-medium text-stone-500 leading-relaxed max-w-xs mx-auto">
              Your emotional landscape will take shape as you journal — keep writing and patterns will surface here.
            </p>
          </div>
        )}

        {!loading && !fetchError && !allZero && data && (
          <div className="relative -mx-6 px-1">
            <svg
              viewBox={`0 0 ${W} ${H}`}
              className="w-full"
              style={{ display: 'block' }}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setHoveredWeek(null)}
              aria-label="Emotional landscape stacked area chart"
            >
              {/* Chart background */}
              <rect
                x={PAD.left} y={PAD.top}
                width={CHART_W} height={CHART_H}
                fill="#FAFAF9"
                rx="8"
              />

              {/* Stacked area bands */}
              {paths.map(({ em, path }) => (
                <path key={em.key} d={path} fill={em.color} opacity={0.82} />
              ))}

              {/* Vertical highlight line on hover */}
              {hoveredWeek !== null && (
                <line
                  x1={xScale(hoveredWeek, n)} y1={PAD.top}
                  x2={xScale(hoveredWeek, n)} y2={PAD.top + CHART_H}
                  stroke="rgba(0,0,0,0.15)"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                />
              )}

              {/* X-axis week labels */}
              {data.map((w, i) => (
                <text
                  key={i}
                  x={xScale(i, n)}
                  y={H - 6}
                  textAnchor="middle"
                  fontSize="10"
                  fill={hoveredWeek === i ? '#44403c' : '#a8a29e'}
                  fontWeight={hoveredWeek === i ? '700' : '500'}
                >
                  {formatWeekLabel(w.week_start)}
                </text>
              ))}

              {/* Tooltip */}
              {hoveredWeek !== null && (() => {
                const w = data[hoveredWeek];
                const tipX = xScale(hoveredWeek, n);
                const tipY = PAD.top + 4;
                const tipW = 142, tipH = 106;
                const flipped = tipX + tipW + 8 > W;
                const tx = flipped ? tipX - tipW - 8 : tipX + 8;

                return (
                  <g>
                    <rect x={tx} y={tipY} width={tipW} height={tipH} rx="8" fill="#1c1917" opacity={0.93} />
                    <text x={tx + 10} y={tipY + 16} fontSize="11" fontWeight="700" fill="white">
                      {formatWeekLabel(w.week_start)}
                    </text>
                    {EMOTIONS.map((em, ei) => (
                      <g key={em.key}>
                        <rect x={tx + 10} y={tipY + 26 + ei * 15} width={8} height={8} rx="2" fill={em.color} />
                        <text x={tx + 22} y={tipY + 34 + ei * 15} fontSize="10" fill="#e7e5e4">
                          {em.label.split(' / ')[0]}: {w[em.key] ?? 0}
                        </text>
                      </g>
                    ))}
                  </g>
                );
              })()}
            </svg>
          </div>
        )}
      </Card>

      {/* Summary cards */}
      {!loading && !fetchError && !allZero && dominant && (
        <div className="grid grid-cols-3 gap-3">
          {/* Dominant pattern */}
          <Card className="p-4 space-y-1">
            <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Dominant pattern</p>
            <p className="text-lg font-bold leading-tight" style={{ color: dominant.color }}>
              {dominant.label.split(' / ')[0]}
            </p>
            <p className="text-[11px] text-stone-400">
              present in {dominantWeeksCount} of 7 weeks
            </p>
          </Card>

          {/* Emotional range */}
          <Card className="p-4 space-y-1">
            <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Tones reflected</p>
            <p className="text-xl font-bold text-stone-700">{rangeCount} of 5</p>
            <p className="text-[11px] text-stone-500 font-medium whitespace-nowrap">
              {rangeCount >= 4 ? 'a rich, varied range' : rangeCount >= 2 ? 'developing breadth' : 'early patterns'}
            </p>
          </Card>

          {/* Notable shift */}
          <Card className="p-4 space-y-1">
            <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Notable shift</p>
            {hasFrustrationShift || hasSadnessShift ? (
              <>
                <p className="text-lg font-bold text-stone-600">{hasFrustrationShift ? 'Stress Eased' : hasSadnessShift ? 'Sadness Eased' : ''}</p>
                <p className="text-[11px] text-stone-400">
                  {hasFrustrationShift   ? 'stress dipped after ' + formatWeekLabel(data[peakFrustrationIdx].week_start) : hasSadnessShift ? 'sadness eased after ' + formatWeekLabel(data[peakSadnessIdx].week_start) : ''}
                </p>
              </>
            ) : (
              <>
                <p className="text-lg font-bold text-stone-400">—</p>
                <p className="text-[11px] text-stone-400">no sharp shifts this period</p>
              </>
            )}
          </Card>
        </div>
      )}

      {/* Disclaimer */}
      {!loading && (
        <p className="text-[10px] text-stone-300 text-center leading-relaxed px-2">
          Based on your journal entries — not a clinical assessment. Mindwire is a self-reflection tool, not a substitute for professional support.
        </p>
      )}
    </div>
    </AiGate>

    {/* Growth Timeline */}
    <div className="mt-8">
      {/* <p className="text-[15px] font-bold text-stone-400 uppercase tracking-[0.18em] border-b border-stone-100 pb-2 mb-6 px-1">
        Your growth
      </p> */}
      <h3 className="text-base font-bold text-stone-800 leading-tight">Your growth</h3>
      <GrowthTimeline userId={userId} aiDataConsent={aiDataConsent} onEnableAI={onEnableAI} />
    </div>
    </>
  );
}

// ─── iOS-style Toggle ─────────────────────────────────────────────────────────
function IOSToggle({ enabled, onToggle, ariaLabel }) {
  return (
    <button
      role="switch"
      aria-checked={enabled}
      aria-label={ariaLabel}
      onClick={onToggle}
      className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400
        ${enabled ? 'bg-teal-500' : 'bg-stone-200'}`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200
          ${enabled ? 'translate-x-6' : 'translate-x-0.5'}`}
      />
    </button>
  );
}

// ─── Settings View ────────────────────────────────────────────────────────────
function SettingsView({ onBack, onNavigateToNotifications, onLogout }) {
  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
      {/* Header */}
      <div className="flex items-center gap-3 px-1 pt-1">
        <button
          onClick={onBack}
          aria-label="Back to profile"
          className="p-2 -ml-2 rounded-full text-stone-500 hover:text-stone-800 hover:bg-stone-100 transition-all duration-200"
        >
          <ArrowLeft size={20} />
        </button>
        <h2 className="text-xl font-bold text-stone-800 tracking-tight">Settings</h2>
      </div>

      {/* Settings rows */}
      <Card className="!p-0 overflow-hidden">
        {/* Notifications row */}
        <button
          onClick={onNavigateToNotifications}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-stone-50 transition-colors duration-150 group"
        >
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-xl bg-amber-100 flex items-center justify-center">
              <Bell size={16} className="text-amber-600" />
            </div>
            <span className="text-sm font-semibold text-stone-800">Notifications</span>
          </div>
          <ChevronRight size={16} className="text-stone-300 group-hover:text-stone-400 transition-colors" />
        </button>

        {/* Divider */}
        <div className="h-px bg-stone-100 mx-5" />

        {/* Sign Out row */}
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-5 py-4 hover:bg-red-50 transition-colors duration-150"
        >
          <div className="h-8 w-8 rounded-xl bg-red-50 flex items-center justify-center">
            <LogOut size={16} className="text-red-400" />
          </div>
          <span className="text-sm font-semibold text-red-500">Sign Out</span>
        </button>
      </Card>
    </div>
  );
}

// ─── Notifications View ───────────────────────────────────────────────────────
const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DAY_NAMES  = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const HOUR_VALUES  = [1,2,3,4,5,6,7,8,9,10,11,12];
const MIN_VALUES   = [5, 15, 30, 45, 60, 90];
const UNIT_OPTIONS = ['Hour', 'Min'];

const ITEM_H = 44; // px — height of each scroll picker row

function ScrollPicker({ options, value, onChange, formatLabel, width = 'w-20' }) {
  const containerRef = useRef(null);
  const timerRef = useRef(null);

  useLayoutEffect(() => {
    const idx = options.indexOf(value);
    if (idx < 0 || !containerRef.current) return;
    containerRef.current.scrollTop = idx * ITEM_H;
  }, [value, options]);

  const handleScroll = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!containerRef.current) return;
      const idx = Math.round(containerRef.current.scrollTop / ITEM_H);
      const clamped = Math.max(0, Math.min(options.length - 1, idx));
      if (options[clamped] !== value) onChange(options[clamped]);
    }, 120);
  };

  return (
    <div className={`relative ${width}`} style={{ height: ITEM_H * 3 }}>
      {/* highlight band — z:1, behind text */}
      <div
        className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 h-11 bg-stone-100 rounded-xl"
        style={{ zIndex: 1 }}
      />
      {/* scrollable content — z:2, above highlight so text is visible */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        style={{
          position: 'absolute',
          inset: 0,
          overflowY: 'scroll',
          scrollSnapType: 'y mandatory',
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
          zIndex: 2,
        }}
      >
        <div style={{ height: ITEM_H }} />
        {options.map((opt) => (
          <div
            key={opt}
            style={{ scrollSnapAlign: 'center', height: ITEM_H }}
            className={`flex items-center justify-center text-sm font-semibold transition-colors duration-150
              ${opt === value ? 'text-stone-800' : 'text-stone-400'}`}
          >
            {formatLabel ? formatLabel(opt) : opt}
          </div>
        ))}
        <div style={{ height: ITEM_H }} />
      </div>
      {/* fades — z:3, on top of text to gracefully fade edge items */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-white to-transparent"
        style={{ zIndex: 3 }}
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white to-transparent"
        style={{ zIndex: 3 }}
      />
    </div>
  );
}

function RepeatEveryPicker({ intervalHrs, intervalMins, onChange }) {
  const unit     = intervalHrs > 0 ? 'Hour' : 'Min';
  const valOpts  = unit === 'Hour' ? HOUR_VALUES : MIN_VALUES;
  const rawValue = unit === 'Hour' ? intervalHrs : intervalMins;
  const safeVal  = valOpts.includes(rawValue) ? rawValue : valOpts[0];

  const handleUnit = (u) => {
    onChange(u === 'Hour'
      ? { intervalHrs: 1,  intervalMins: 0  }
      : { intervalHrs: 0,  intervalMins: 30 }
    );
  };

  const handleValue = (v) => {
    onChange(unit === 'Hour'
      ? { intervalHrs: v, intervalMins: 0 }
      : { intervalHrs: 0, intervalMins: v }
    );
  };

  return (
    <div className="flex flex-row items-center justify-center gap-3 py-2 px-4 bg-white/50 rounded-2xl border border-stone-100">
      <p className="text-[10px] font-bold text-stone-400 uppercase tracking-[0.14em]">Repeat Every</p>
      <div className="flex items-center gap-1">
        <ScrollPicker options={valOpts}  value={safeVal} onChange={handleValue} width="w-20" />
        <ScrollPicker options={UNIT_OPTIONS} value={unit} onChange={handleUnit} width="w-16" />
      </div>
    </div>
  );
}

function ReminderCard({ title, icon, iconBg, enabled, onToggle, children }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-3">
          <div className={`h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg ?? 'bg-stone-50'}`}>
            {icon}
          </div>
          <div>
            <p className="text-sm font-semibold text-stone-800">{title}</p>
            <p className="text-xs text-stone-400 mt-0.5">{enabled ? 'On' : 'Off'}</p>
          </div>
        </div>
        <IOSToggle enabled={enabled} onToggle={onToggle} ariaLabel={`Toggle ${title}`} />
      </div>

      {enabled && (
        <>
          <div className="h-px bg-stone-50" />
          <div className="px-5 py-5 space-y-5">
            {children}
          </div>
        </>
      )}
    </div>
  );
}

function DaySelector({ days, onDayToggle }) {
  return (
    <div className="flex flex-col items-center gap-2.5">
      <p className="text-[10px] font-bold text-stone-400 uppercase tracking-[0.14em]">Which days?</p>
      <div className="flex gap-1.5">
        {DAY_LABELS.map((label, idx) => (
          <button
            key={idx}
            onClick={() => onDayToggle(idx)}
            aria-pressed={days.includes(idx)}
            className={`h-9 w-9 rounded-full text-xs font-bold transition-all duration-150 flex items-center justify-center
              ${days.includes(idx)
                ? 'bg-teal-500 text-white shadow-sm'
                : 'bg-stone-100 text-stone-500 hover:bg-stone-200'}`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function TimePicker({ label, value, onChange, ariaLabel }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <p className="text-[10px] font-bold text-stone-400 uppercase tracking-[0.14em]">{label}</p>
      <input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
        className="bg-stone-100 rounded-xl px-4 py-2 text-sm font-semibold text-stone-700 border-none outline-none focus:ring-2 focus:ring-teal-400 cursor-pointer"
      />
    </div>
  );
}

const LEAD_OPTIONS = [0, 10, 20, 30, 40, 50, 60];

function LeadMinutesPicker({ value, onChange }) {
  const safeValue = LEAD_OPTIONS.includes(value) ? value : 0;
  return (
    <div className="flex flex-row items-center justify-center gap-3 py-2 px-4 bg-white/50 rounded-2xl border border-stone-100">
      <p className="text-[10px] font-bold text-stone-400 uppercase tracking-[0.14em]">Remind me before</p>
      <ScrollPicker
        options={LEAD_OPTIONS}
        value={safeValue}
        onChange={onChange}
        // formatLabel={(v) => v === 0 ? 'None' : `${v}`}
      />
      <p className="text-[10px] font-bold text-stone-400 uppercase tracking-[0.14em]">MINUTES</p>
    </div>
  );
}

function NotificationsView({ userId, onBack }) {
  const [prefs, setPrefs] = useState({
    self_reflection: { enabled: false, days: [1, 2, 3, 4, 5], time: '08:00', leadMinutes: 0 },
    mindfulness:     { enabled: false, days: [1,2,3,4,5], startTime: '09:00', endTime: '18:00', intervalHrs: 1, intervalMins: 0 },
    bedtime:         { enabled: false, time: '21:00' },
    // The prompt reminder is its own kind, on by default at 8 PM every day —
    // matching 20260917_daily_prompt_reminder.sql and the lazy seed in
    // GET /user/:id/notification-preferences. Change all three together.
    daily_prompt:    { enabled: true, days: [0, 1, 2, 3, 4, 5, 6], time: '19:45' },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permissionState, setPermissionState] = useState('default');
  const [pushSupported, setPushSupported] = useState(false);
  // Whether THIS browser can actually receive a push. Tracked separately from
  // permission: a granted permission with no subscription still delivers
  // nothing, which is exactly the state 5 of 6 web users were silently in.
  const [hasSubscription, setHasSubscription] = useState(null);
  const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true;

  useEffect(() => {
    setPushSupported('PushManager' in window && 'serviceWorker' in navigator);
    if ('Notification' in window) setPermissionState(Notification.permission);
    // Null until known, so the notice below never flashes before we've checked.
    getCurrentPushSubscription()
      .then((sub) => setHasSubscription(!!sub))
      .catch(() => setHasSubscription(false));

    api.get(`/user/${userId}/notification-preferences`)
      .then((res) => {
        const rows = res.data?.preferences ?? [];
        if (rows.length === 0) return;
        setPrefs((prev) => {
          const next = { ...prev };
          for (const row of rows) {
            if (!next[row.reminder_type]) continue;
            if (row.reminder_type === 'mindfulness') {
              const totalMins = row.reminder_lead_minutes ?? 60;
              let iHrs = 1, iMins = 0;
              const hVal = totalMins / 60;
              if (totalMins > 0 && totalMins % 60 === 0 && HOUR_VALUES.includes(hVal)) {
                iHrs = hVal; iMins = 0;
              } else if (totalMins > 0) {
                iHrs = 0;
                iMins = MIN_VALUES.includes(totalMins)
                  ? totalMins
                  : MIN_VALUES.reduce((a, b) => Math.abs(b - totalMins) < Math.abs(a - totalMins) ? b : a);
              }
              next.mindfulness = {
                enabled: row.enabled,
                days: row.days_of_week ?? [1,2,3,4,5],
                startTime: (row.time_of_day ?? '09:00:00').slice(0, 5),
                endTime: (row.time_of_day_2 ?? '18:00:00').slice(0, 5),
                intervalHrs: iHrs,
                intervalMins: iMins,
              };
            } else {
              next[row.reminder_type] = {
                enabled: row.enabled,
                days: row.days_of_week ?? prev[row.reminder_type].days,
                time: (row.time_of_day ?? '08:00:00').slice(0, 5),
                ...(row.reminder_type === 'self_reflection' && { leadMinutes: row.reminder_lead_minutes ?? 0 }),
              };
            }
          }
          return next;
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const savePrefs = useCallback(async (updatedPrefs) => {
    setSaving(true);
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const payload = [
      {
        reminder_type: 'self_reflection',
        enabled: updatedPrefs.self_reflection.enabled,
        days_of_week: updatedPrefs.self_reflection.days,
        time_of_day: updatedPrefs.self_reflection.time + ':00',
        time_of_day_2: null,
        reminder_lead_minutes: updatedPrefs.self_reflection.leadMinutes ?? 0,
        timezone,
      },
      {
        reminder_type: 'mindfulness',
        enabled: updatedPrefs.mindfulness.enabled,
        days_of_week: updatedPrefs.mindfulness.days,
        time_of_day: updatedPrefs.mindfulness.startTime + ':00',
        time_of_day_2: updatedPrefs.mindfulness.endTime + ':00',
        reminder_lead_minutes: updatedPrefs.mindfulness.intervalHrs * 60 + updatedPrefs.mindfulness.intervalMins,
        timezone,
      },
      {
        reminder_type: 'bedtime',
        enabled: updatedPrefs.bedtime.enabled,
        days_of_week: [0, 1, 2, 3, 4, 5, 6],
        time_of_day: updatedPrefs.bedtime.time + ':00',
        time_of_day_2: null,
        reminder_lead_minutes: 0,
        timezone,
      },
      {
        reminder_type: 'daily_prompt',
        enabled: updatedPrefs.daily_prompt.enabled,
        days_of_week: updatedPrefs.daily_prompt.days,
        time_of_day: updatedPrefs.daily_prompt.time + ':00',
        time_of_day_2: null,
        reminder_lead_minutes: 0,
        timezone,
      },
    ];
    await api.post(`/user/${userId}/notification-preferences`, { preferences: payload }).catch(() => {});
    setSaving(false);
  }, [userId]);

  const handleToggle = useCallback(async (type) => {
    const wasOff = !prefs[type].enabled;
    const updated = { ...prefs, [type]: { ...prefs[type], enabled: !prefs[type].enabled } };
    setPrefs(updated);

    if (wasOff && pushSupported) {
      try {
        const sub = await subscribeToPush();
        if (sub) {
          setPermissionState('granted');
          setHasSubscription(true);
          await api.post(`/user/${userId}/push-subscription`, sub.toJSON()).catch(() => {});
        } else {
          if ('Notification' in window) setPermissionState(Notification.permission);
        }
      } catch {}
    }

    savePrefs(updated);
  }, [prefs, pushSupported, userId, savePrefs]);

  const handleDayToggle = useCallback((type, dayIdx) => {
    const currentDays = prefs[type].days;
    const newDays = currentDays.includes(dayIdx)
      ? currentDays.filter((d) => d !== dayIdx)
      : [...currentDays, dayIdx].sort();
    const updated = { ...prefs, [type]: { ...prefs[type], days: newDays } };
    setPrefs(updated);
    savePrefs(updated);
  }, [prefs, savePrefs]);

  const handleTimeChange = useCallback((type, field, time) => {
    const updated = { ...prefs, [type]: { ...prefs[type], [field]: time } };
    setPrefs(updated);
    savePrefs(updated);
  }, [prefs, savePrefs]);

  const handleLeadChange = useCallback((mins) => {
    const updated = { ...prefs, self_reflection: { ...prefs.self_reflection, leadMinutes: mins } };
    setPrefs(updated);
    savePrefs(updated);
  }, [prefs, savePrefs]);

  const handleMindfulField = useCallback((field, value) => {
    const updated = { ...prefs, mindfulness: { ...prefs.mindfulness, [field]: value } };
    setPrefs(updated);
    savePrefs(updated);
  }, [prefs, savePrefs]);

  const handleMindfulInterval = useCallback((intervalUpdate) => {
    const updated = { ...prefs, mindfulness: { ...prefs.mindfulness, ...intervalUpdate } };
    setPrefs(updated);
    savePrefs(updated);
  }, [prefs, savePrefs]);

  const handleMindfulDayToggle = useCallback((dayIdx) => {
    const days = prefs.mindfulness.days;
    const newDays = days.includes(dayIdx) ? days.filter(d => d !== dayIdx) : [...days, dayIdx].sort((a,b) => a-b);
    const updated = { ...prefs, mindfulness: { ...prefs.mindfulness, days: newDays } };
    setPrefs(updated);
    savePrefs(updated);
  }, [prefs, savePrefs]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 px-1 pt-1">
          <button onClick={onBack} className="p-2 -ml-2 rounded-full text-stone-500 hover:bg-stone-100 transition-all">
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-xl font-bold text-stone-800 tracking-tight">Notifications</h2>
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 rounded-2xl bg-stone-100 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300">
      {/* Header */}
      <div className="flex items-center justify-between px-1 pt-1">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            aria-label="Back to settings"
            className="p-2 -ml-2 rounded-full text-stone-500 hover:text-stone-800 hover:bg-stone-100 transition-all duration-200"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-xl font-bold text-stone-800 tracking-tight">Notifications</h2>
        </div>
        {saving && <span className="text-xs text-stone-400 animate-pulse">Saving…</span>}
      </div>

      {/* Subtitle */}
      <p className="text-sm text-stone-500 leading-relaxed px-1">
        Reflect, breathe, and rest — let MindWire gently remind you.
      </p>

      {/* Permission denied banner */}
      {permissionState === 'denied' && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-100 rounded-2xl">
          <Bell size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-700 leading-relaxed">
            Notifications are blocked. Open your browser settings and allow notifications for this site to receive reminders.
          </p>
        </div>
      )}

      {/* Saved, but this browser cannot deliver.
          The schedule is shared with the phone app, so we deliberately do NOT
          block the save — a web user configuring their phone's reminders is a
          real and valid thing to do. What was wrong before was the silence:
          the switch read "On" while nothing anywhere could deliver it. */}
      {hasSubscription === false && permissionState !== 'denied' &&
        (prefs.self_reflection.enabled || prefs.mindfulness.enabled
          || prefs.bedtime.enabled || prefs.daily_prompt.enabled) && (
        <div className="flex items-start gap-3 p-4 bg-stone-50 border border-stone-100 rounded-2xl">
          <Bell size={16} className="text-stone-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-stone-500 leading-relaxed">
            Your schedule is saved, but <strong>this browser can't deliver reminders</strong>.
            They'll still arrive on your phone if you have the Mindwire app. To get them
            here too, allow notifications for this site.
          </p>
        </div>
      )}

      {/* PWA / iOS tip */}
      {(!pushSupported || !isStandalone) && (
        <div className="flex items-start gap-3 p-4 bg-stone-50 border border-stone-100 rounded-2xl">
          <Moon size={16} className="text-stone-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-stone-500 leading-relaxed">
            To receive reminders on your iPhone lock screen, add MindWire to your Home Screen — tap Share and choose <strong>Add to Home Screen</strong>.
          </p>
        </div>
      )}

      {/* Self Reflection Schedule */}
      <div>
        <SectionHeading>Self Reflection Schedule</SectionHeading>
        <ReminderCard
          title="Self Reflection"
          iconBg="bg-teal-50"
          icon={<Leaf size={15} className="text-teal-600" />}
          enabled={prefs.self_reflection.enabled}
          onToggle={() => handleToggle('self_reflection')}
        >
          <DaySelector
            days={prefs.self_reflection.days}
            onDayToggle={(d) => handleDayToggle('self_reflection', d)}
          />
          <TimePicker
            label="Time"
            value={prefs.self_reflection.time}
            onChange={(t) => handleTimeChange('self_reflection', 'time', t)}
            ariaLabel="Self reflection time"
          />
          <LeadMinutesPicker
            value={prefs.self_reflection.leadMinutes}
            onChange={handleLeadChange}
          />
        </ReminderCard>
      </div>

      {/* Other Reminders */}
      <div className="space-y-3">
        <SectionHeading>Other Reminders</SectionHeading>

        <ReminderCard
          title="Mindfulness Routine"
          iconBg="bg-[#EBF2EE]"
          icon={<Leaf size={15} className="text-[#6B9E8B]" />}
          enabled={prefs.mindfulness.enabled}
          onToggle={() => handleToggle('mindfulness')}
        >
          {/* Days */}
          <div className="flex flex-col items-center gap-2.5">
            <p className="text-[10px] font-bold text-stone-400 uppercase tracking-[0.14em]">Which days?</p>
            <div className="flex gap-1.5">
              {DAY_LABELS.map((label, idx) => (
                <button
                  key={idx}
                  onClick={() => handleMindfulDayToggle(idx)}
                  aria-pressed={prefs.mindfulness.days.includes(idx)}
                  aria-label={DAY_NAMES[idx]}
                  className={`h-9 w-9 rounded-full text-xs font-bold transition-all duration-150 flex items-center justify-center
                    ${prefs.mindfulness.days.includes(idx)
                      ? 'bg-[#6B9E8B] text-white shadow-sm'
                      : 'bg-stone-100 text-stone-500 hover:bg-stone-200'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Start + End time */}
          <div className="flex justify-center gap-8">
            <TimePicker
              label="Start Time"
              value={prefs.mindfulness.startTime}
              onChange={(t) => handleMindfulField('startTime', t)}
              ariaLabel="Mindfulness start time"
            />
            <TimePicker
              label="End Time"
              value={prefs.mindfulness.endTime}
              onChange={(t) => handleMindfulField('endTime', t)}
              ariaLabel="Mindfulness end time"
            />
          </div>

          {/* Repeat interval */}
          <RepeatEveryPicker
            intervalHrs={prefs.mindfulness.intervalHrs}
            intervalMins={prefs.mindfulness.intervalMins}
            onChange={handleMindfulInterval}
          />

          {/* Schedule summary */}
          {prefs.mindfulness.days.length > 0 && (() => {
            const [sh, sm] = prefs.mindfulness.startTime.split(':').map(Number);
            const [eh, em] = prefs.mindfulness.endTime.split(':').map(Number);
            const fmt = (h, m) => `${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`;
            const interval = prefs.mindfulness.intervalHrs > 0
              ? `${prefs.mindfulness.intervalHrs} ${prefs.mindfulness.intervalHrs === 1 ? 'hour' : 'hours'}`
              : `${prefs.mindfulness.intervalMins} min`;
            const days = prefs.mindfulness.days.map(i => DAY_NAMES[i].slice(0,3)).join(', ');
            return (
              <div className="bg-[#EBF2EE] rounded-xl px-4 py-3">
                <p className="text-xs text-[#4A7869] leading-relaxed text-center">
                  Every {days}, {fmt(sh,sm)} → {fmt(eh,em)}, every {interval}
                </p>
              </div>
            );
          })()}
        </ReminderCard>

        <ReminderCard
          title="Bedtime Reminder"
          iconBg="bg-stone-50"
          icon={<Moon size={15} className="text-stone-400" />}
          enabled={prefs.bedtime.enabled}
          onToggle={() => handleToggle('bedtime')}
        >
          <TimePicker
            label="Time"
            value={prefs.bedtime.time}
            onChange={(t) => handleTimeChange('bedtime', 'time', t)}
            ariaLabel="Bedtime reminder time"
          />
        </ReminderCard>

        <ReminderCard
          title="Daily Reflection Prompt"
          iconBg="bg-amber-50"
          icon={<Sparkles size={15} className="text-amber-500" />}
          enabled={prefs.daily_prompt.enabled}
          onToggle={() => handleToggle('daily_prompt')}
        >
          <p className="text-xs text-stone-400 leading-relaxed">
            A question drawn from your own reflections. Opens ready to answer.
          </p>
          <DaySelector
            days={prefs.daily_prompt.days}
            onDayToggle={(d) => handleDayToggle('daily_prompt', d)}
          />
          <TimePicker
            label="Time"
            value={prefs.daily_prompt.time}
            onChange={(t) => handleTimeChange('daily_prompt', 'time', t)}
            ariaLabel="Daily reflection prompt time"
          />
        </ReminderCard>
      </div>

      <p className="text-[11px] text-stone-400 text-center pb-2">
        Times are in your device's local timezone
      </p>
    </div>
  );
}

// ─── Main ProfilePage ─────────────────────────────────────────────────────────
const TABS = [
  { id: 'stats',    label: 'Journal Stats' },
  { id: 'identity', label: 'Identity' },
  { id: 'safety',   label: 'Safety' },
  { id: 'insights', label: 'Deep Insights'  },
];

// ─── Profile photo ───────────────────────────────────────────────────────────
// The avatar is the control: click it to choose a photo, and a small ✕ appears
// on top once there is one to remove. No separate "Edit photo" row — it would
// say the same thing twice.
const MAX_PHOTO_BYTES = 8 * 1024 * 1024;

function ProfilePhotoPicker({ url, fallback, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = async (work) => {
    setBusy(true);
    setError('');
    try {
      onChange?.(await work());
    } catch {
      setError("That didn't work. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const pick = async (e) => {
    const file = e.target.files?.[0];
    // Reset immediately: picking the same file twice must still fire onChange.
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) {
      setError('That photo is over 8MB. Try a smaller one.');
      return;
    }
    await run(async () => {
      const form = new FormData();
      // Never set Content-Type by hand — the browser owns the multipart boundary.
      form.append('image', file, file.name || 'photo.jpg');
      const res = await api.post('/api/uploadAvatar', form);
      return res.data?.url || '';
    });
  };

  return (
    <div className="flex-shrink-0">
      <div className="relative">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          aria-label={url ? 'Change profile photo' : 'Add a profile photo'}
          className="group relative h-14 w-14 sm:h-18 sm:w-18 rounded-full bg-gradient-to-br from-stone-200 to-stone-300 flex items-center justify-center text-xl sm:text-2xl font-bold text-stone-500 shadow-inner ring-2 ring-white overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C9A82] disabled:opacity-60"
        >
          <ProfileAvatar url={url} fallback={fallback} />
          <span className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/30 transition-colors flex items-center justify-center">
            <Camera size={18} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </span>
        </button>

        {/* Touch devices never hover, so the camera cue has to be permanent. */}
        <span className="absolute -bottom-0.5 -right-0.5 h-5 w-5 rounded-full bg-[#7C9A82] ring-2 ring-white flex items-center justify-center pointer-events-none">
          <Camera size={11} className="text-white" />
        </span>

        {url && !busy && (
          <button
            type="button"
            onClick={() => run(async () => { await api.delete('/api/avatar'); return ''; })}
            aria-label="Remove profile photo"
            title="Remove photo"
            className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-white ring-1 ring-stone-200 shadow-sm flex items-center justify-center text-stone-500 hover:text-red-500 transition-colors"
          >
            <X size={11} strokeWidth={2.5} />
          </button>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={pick}
          className="hidden"
        />
      </div>

      {(busy || error) && (
        <p role="status" className={`mt-1 text-[10px] font-semibold ${error ? 'text-red-500' : 'text-stone-400'}`}>
          {error || 'Uploading…'}
        </p>
      )}
    </div>
  );
}

export function ProfilePage({
  userId,
  userEmail,
  profile,
  isGuest,
  isProfileDirty,
  isNameValid,
  updateProfileField,
  persistProfile,
  onPhotoChange,
  onProfileReset,
  onLogout,
  onNavigateToJournal,
  aiDataConsent,
  onConsentChange,
  requestTab,
}) {
  const [activeTab, setActiveTab] = useState('stats');
  const [view, setView] = useState('profile'); // 'profile' | 'settings' | 'notifications'

  useEffect(() => {
    if (requestTab) { setActiveTab(requestTab); setView('profile'); }
  }, [requestTab]);

  if (view === 'settings') {
    return (
      <SettingsView
        onBack={() => setView('profile')}
        onNavigateToNotifications={() => setView('notifications')}
        onLogout={onLogout}
      />
    );
  }

  if (view === 'notifications') {
    return (
      <NotificationsView
        userId={userId}
        onBack={() => setView('settings')}
      />
    );
  }

  return (
    <div className="space-y-3">
      {/* ── Profile Header ── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-stone-50 via-white to-stone-100 rounded-2xl border border-stone-100 shadow-sm px-4 py-4">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-amber-100/40 blur-3xl pointer-events-none" />
        {/* Settings gear icon */}
        <button
          onClick={() => setView('settings')}
          aria-label="Open settings"
          className="absolute top-3 right-3 p-2 rounded-full text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-all duration-200 z-10"
        >
          <Settings size={18} />
        </button>
        <div className="flex items-center gap-4">
          {/* Avatar — also where the photo is changed */}
          <ProfilePhotoPicker
            url={profile.avatar_url}
            fallback={profile.name ? profile.name[0].toUpperCase() : <User size={22} />}
            onChange={onPhotoChange}
          />
          {/* Name + login id + tagline */}
          <div className="min-w-0 flex-1 pr-8">
            <h2 className="text-xl sm:text-3xl font-black uppercase tracking-[0.05em] text-stone-900 leading-tight truncate">
              {profile.name || 'Your Name'}
            </h2>
            {/* Login id — hidden for guests (synthetic guest_*@mindwire.local address) */}
            {!isGuest && (profile.email || userEmail) && (
              <p className="text-[11px] sm:text-xs font-medium text-stone-500 mt-0.5 truncate" title={profile.email || userEmail}>
                {profile.email || userEmail}
              </p>
            )}
            {profile.occupation && (
              <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest mt-0.5 truncate">
                {profile.occupation}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Tab Bar ── */}
      {(() => {
        const visibleTabs = isGuest ? TABS.filter(t => t.id !== 'safety') : TABS;
        return <div className="flex bg-white rounded-xl border border-stone-100 shadow-sm overflow-hidden">
          {visibleTabs.map((tab, i) => (
            <div key={tab.id} className="flex items-stretch flex-1">
              {i > 0 && <div className="w-px bg-stone-100 self-stretch" />}
              <button
                onClick={() => setActiveTab(tab.id)}
                aria-selected={activeTab === tab.id}
                role="tab"
                className={`flex-1 py-3 px-1 text-[10px] sm:text-xs font-bold tracking-[0.08em] uppercase transition-all duration-200 relative leading-tight
                  ${activeTab === tab.id ? 'text-stone-800' : 'text-stone-400 hover:text-stone-600'}`}
              >
                [{tab.label}]
                {activeTab === tab.id && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-[2px] rounded-full bg-stone-700" />
                )}
              </button>
            </div>
          ))}
        </div>;
      })()}

      {/* ── Tab Content ── */}
      {activeTab === 'stats'    && <JournalStatsTab userId={userId} onNavigateToJournal={onNavigateToJournal} />}
      {activeTab === 'identity' && (
        isGuest ? (
          <div className="bg-white rounded-[2rem] border border-stone-100 shadow-sm p-8 text-center space-y-4">
            <div className="h-12 w-12 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto">
              <User size={22} className="text-stone-400" />
            </div>
            <div>
              <h3 className="text-base font-bold text-stone-800">Create an account to save your profile</h3>
              <p className="text-sm text-stone-500 mt-1">Your guest session expires in 24 hours. Sign up to keep your entries and personalize your experience.</p>
            </div>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white shadow-md transition-all duration-200 active:scale-[0.97]"
              style={{ background: 'linear-gradient(135deg, #7C9A82, #5a7a60)' }}
            >
              Create account
            </button>
          </div>
        ) : (
          <IdentityTab
            profile={profile}
            isProfileDirty={isProfileDirty}
            isNameValid={isNameValid}
            updateProfileField={updateProfileField}
            persistProfile={persistProfile}
            onProfileReset={onProfileReset}
          />
        )
      )}
      {activeTab === 'safety'   && !isGuest && <SafetyTab userId={userId} onLogout={onLogout} aiDataConsent={aiDataConsent} onConsentChange={onConsentChange} />}
      {activeTab === 'insights' && <DeepInsightsTab userId={userId} aiDataConsent={aiDataConsent} onEnableAI={() => setActiveTab('safety')} />}

      {userEmail && (
        <p className="text-[11px] text-stone-400 text-center pt-2 pb-1">
          Logged in as <span className="font-medium">{userEmail}</span>
        </p>
      )}
    </div>
  );
}
